// Drop-handler math for dnd-kit (Doc 06 §7). This is the ONE place the web app
// resolves a drag-end event into a store command:
//   - rows (items/todos):   onMove(rowId, toCategoryId, neighbors) → store `move`
//     → a single {categoryId, order} patch (order = between(neighbors) in the store)
//   - categories:           onReorder(categoryId, between(prev, next)) → store `reorder`
//     → only the moved category's order is written
//
// All fractional-index math is `between` from @pack-it-up/shared — the shared ordering
// code. This module only turns a drop position into neighbours; it never computes
// indices itself and never touches Firebase or the stores directly.

import type { DragEndEvent } from "@dnd-kit/core";
import { between } from "@pack-it-up/shared";
import type { CategoryId, FractionalIndex, NoteId } from "@pack-it-up/shared";
import type { Neighbors } from "@pack-it-up/viewmodel";
import { byOrder } from "@pack-it-up/shared";

// The minimal row shape the drop math needs — PackingItem and TodoItem both satisfy it.
export interface DraggableRowLike {
  id: string;
  order: FractionalIndex;
  categoryId: CategoryId;
}

// The minimal category shape (Category satisfies it).
export interface DraggableCategoryLike {
  id: string;
  order: FractionalIndex;
}

export interface RowDrop {
  toCategoryId: CategoryId;
  neighbors: Neighbors;
}

/**
 * Resolve a row drag-end into its destination category + fractional-index neighbours.
 *
 * `overId` is either another row's id (drop onto/next to a row) or a category id
 * (drop into an empty category / the category container). Returns null when the drop
 * resolves to nothing actionable (unknown ids).
 *
 * Within-category: the result's `toCategoryId` equals the row's current category —
 * the store still issues one {categoryId, order} write (categoryId unchanged).
 * Cross-category: `toCategoryId` is the destination; same single write.
 */
export const rowDropTarget = (
  rows: readonly DraggableRowLike[],
  categories: readonly DraggableCategoryLike[],
  activeId: string,
  overId: string,
): RowDrop | null => {
  const activeRow = rows.find((r) => r.id === activeId);
  if (!activeRow) return null;

  // Destination category: the over-row's category, or the category itself.
  const overRow = rows.find((r) => r.id === overId);
  const overCategory = categories.find((c) => c.id === overId);
  if (overRow === undefined && overCategory === undefined) return null;
  const toCategoryId = (overRow?.categoryId ?? overCategory!.id) as CategoryId;

  // Rows already in the destination, excluding the dragged row, in stored order.
  const destRows = rows.filter((r) => r.categoryId === toCategoryId && r.id !== activeId).sort(byOrder);

  let insertIndex: number;
  if (overRow !== undefined) {
    const overIdx = destRows.findIndex((r) => r.id === overId);
    if (overIdx === -1) {
      insertIndex = destRows.length; // defensive: over-row vanished — append
    } else if (activeRow.categoryId !== toCategoryId) {
      // Cross-category: land at the over row's slot (it shifts down).
      insertIndex = overIdx;
    } else {
      // Within-category: direction decides before/after the over row —
      // dnd-kit swaps the dragged row into the over slot.
      const allInDest = rows.filter((r) => r.categoryId === toCategoryId).sort(byOrder);
      const origActiveIdx = allInDest.findIndex((r) => r.id === activeId);
      const origOverIdx = allInDest.findIndex((r) => r.id === overId);
      insertIndex = origActiveIdx < origOverIdx ? overIdx + 1 : overIdx;
    }
  } else {
    // Dropped on the category itself (e.g. empty category): append.
    insertIndex = destRows.length;
  }

  const before = destRows[insertIndex - 1]?.order;
  const after = destRows[insertIndex]?.order;
  return {
    toCategoryId,
    neighbors: {
      ...(before !== undefined ? { before } : {}),
      ...(after !== undefined ? { after } : {}),
    },
  };
};

/**
 * Resolve a category drag-end into the moved category's new fractional index.
 * Returns null when the ids don't resolve. One `reorder` write per drop.
 */
export const categoryDropOrder = (
  categories: readonly DraggableCategoryLike[],
  activeId: string,
  overId: string,
): FractionalIndex | null => {
  const sorted = [...categories].sort(byOrder);
  const origActiveIdx = sorted.findIndex((c) => c.id === activeId);
  const origOverIdx = sorted.findIndex((c) => c.id === overId);
  if (origActiveIdx === -1 || origOverIdx === -1) return null;

  const withoutActive = sorted.filter((c) => c.id !== activeId);
  const overIdx = withoutActive.findIndex((c) => c.id === overId);
  const insertIndex = origActiveIdx < origOverIdx ? overIdx + 1 : overIdx;

  const before = withoutActive[insertIndex - 1]?.order ?? null;
  const after = withoutActive[insertIndex]?.order ?? null;
  return between(before, after);
};

/**
 * dnd-kit onDragEnd handler for row (item/todo) drags. `onMove` maps 1:1 to the
 * ViewModel's `move(rowId, toCategoryId, neighbors)` — exactly one call per drop.
 */
export const makeRowDragEnd =
  (
    rows: readonly DraggableRowLike[],
    categories: readonly DraggableCategoryLike[],
    onMove: (rowId: string, toCategoryId: CategoryId, neighbors: Neighbors) => void,
  ) =>
  (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const drop = rowDropTarget(rows, categories, String(active.id), String(over.id));
    if (drop !== null) onMove(String(active.id), drop.toCategoryId, drop.neighbors);
  };

/**
 * dnd-kit onDragEnd handler for category drags. `onReorder` maps 1:1 to the
 * ViewModel's `reorder(categoryId, order)` — only the moved category's order.
 */
export const makeCategoryDragEnd =
  (
    categories: readonly DraggableCategoryLike[],
    onReorder: (categoryId: CategoryId, order: FractionalIndex) => void,
  ) =>
  (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const order = categoryDropOrder(categories, String(active.id), String(over.id));
    if (order !== null) onReorder(String(active.id) as CategoryId, order);
  };

/**
 * dnd-kit onDragEnd handler for note drags — a flat list, same single-level reorder
 * math as categories (`categoryDropOrder` only ever looks at `{id, order}`, so it's
 * reused as-is here rather than duplicated).
 */
export const makeNoteDragEnd =
  (
    notes: readonly DraggableCategoryLike[],
    onReorder: (noteId: NoteId, order: FractionalIndex) => void,
  ) =>
  (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const order = categoryDropOrder(notes, String(active.id), String(over.id));
    if (order !== null) onReorder(String(active.id) as NoteId, order);
  };
